// Le passkey (WebAuthn) dal browser: il server manda le opzioni in JSON con i byte in base64url,
// il browser vuole ArrayBuffer; la risposta torna al server di nuovo in base64url. Conversione a
// mano: `parseCreationOptionsFromJSON` e `toJSON` mancano ancora su parte dei browser in uso.
import { api } from './api';

const fromB64 = (s: string): ArrayBuffer => {
  const pad = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
  const bin = atob(pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
};

const toB64 = (b: ArrayBuffer | null | undefined): string => {
  if (!b) return '';
  let bin = '';
  for (const x of new Uint8Array(b)) bin += String.fromCharCode(x);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

interface Descriptor {
  id: string;
  type: 'public-key';
  transports?: AuthenticatorTransport[];
}

const descriptors = (list: unknown): PublicKeyCredentialDescriptor[] =>
  ((list as Descriptor[] | undefined) ?? []).map((d) => ({ ...d, id: fromB64(d.id) }));

export const passkeysSupported = (): boolean =>
  typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function';

/** Crea una passkey per l'account aperto e la registra con il nome dato. */
export async function registerPasskey(name: string): Promise<void> {
  const o = (await api.passkeyOptions()) as Record<string, unknown> & {
    challenge: string;
    user: { id: string; name: string; displayName: string };
  };
  const cred = (await navigator.credentials.create({
    publicKey: {
      ...(o as unknown as PublicKeyCredentialCreationOptions),
      challenge: fromB64(o.challenge),
      user: { ...o.user, id: fromB64(o.user.id) },
      excludeCredentials: descriptors(o.excludeCredentials),
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('Passkey non creata.');
  const r = cred.response as AuthenticatorAttestationResponse;
  await api.addPasskey(
    {
      id: cred.id,
      rawId: toB64(cred.rawId),
      type: cred.type,
      response: {
        clientDataJSON: toB64(r.clientDataJSON),
        attestationObject: toB64(r.attestationObject),
        transports: r.getTransports?.() ?? [],
      },
      clientExtensionResults: cred.getClientExtensionResults(),
    },
    name,
  );
}

/** Entra con una passkey: il browser chiede quale, il server apre la sessione. */
export async function loginWithPasskey(): Promise<void> {
  const o = (await api.passkeyLoginOptions()) as Record<string, unknown> & { challenge: string };
  const cred = (await navigator.credentials.get({
    publicKey: {
      ...(o as unknown as PublicKeyCredentialRequestOptions),
      challenge: fromB64(o.challenge),
      allowCredentials: descriptors(o.allowCredentials),
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('Nessuna passkey scelta.');
  const r = cred.response as AuthenticatorAssertionResponse;
  await api.passkeyLogin({
    id: cred.id,
    rawId: toB64(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: toB64(r.clientDataJSON),
      authenticatorData: toB64(r.authenticatorData),
      signature: toB64(r.signature),
      userHandle: toB64(r.userHandle),
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  });
}

/** Il nome proposto per una passkey nuova: il dispositivo da cui la si crea. */
export function deviceName(): string {
  const a = navigator.userAgent;
  if (/iPhone/.test(a)) return 'iPhone';
  if (/iPad/.test(a)) return 'iPad';
  if (/Android/.test(a)) return 'Android';
  if (/Mac OS X/.test(a)) return 'Mac';
  if (/Windows/.test(a)) return 'Windows';
  return 'Passkey';
}
